import React, { useState, useEffect, useMemo, forwardRef } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { Tooltip, Flex, Affix } from "antd"
import { motion } from "framer-motion"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { Badge } from "components-design-system"

// custom
import primeLogoSrc from "../../assets/images/prime-logo.svg"
import pLogoSrc from "../../assets/images/p-prime-logo.svg"
import primeStar from "../../assets/images/prime-star.svg" // TODO: remove asset
import "./sidebar.scss"

// context
import useAppState from "context/hooks/useAppState"

// api
import { useUserPermissions } from "api/hooks"

/** Might bring these back... do not delete
 * Reports > Studies links to following API endpoints removed temporarily:
 * * /l/data-change-report
 * * /l/data-quality-report
 * * /l/new-study-report
 *
 *
 * * dataload/study-data-report.pug
 * * dataload/data-quality-report.pug
 * * dataload/new-study-report.pug
 */

import navbarData from "constants/pageData"
import { COLORS } from "constants/sidebar"

const Sidebar = forwardRef((props, ref) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [selectedNav, setSelectedNav] = useState("")
  const [permissions, setPermissions] = useState(null)
  const { sidebarOpen, setSidebarOpen, publicEnvVars } = useAppState()
  const { data: userPermissions } = useUserPermissions()
  // show/hide new feature indicators
  const [showNew, setShowNew] = useState(true)

  // change the variable name "_01" when there is a new item added
  const NEW_FEATURES_SEEN = "new_features_seen_01"

  // if localStorage set, hide "New" tags and dots
  useEffect(() => {
    if (localStorage.getItem(NEW_FEATURES_SEEN) == "true") {
      setShowNew(false)
    } else {
      localStorage.setItem(NEW_FEATURES_SEEN, true)
    }
  }, [])

  const hasNewItem = (navItem) => {
    let hasNew = false
    if (navItem.isNew) return true
    if (navItem.items) {
      navItem.items.forEach((item) => {
        if (!!item.isNew) hasNew = true
      })
    }
    return hasNew
  }

  // animation props
  // TODO: change from framer-motion to css animations if any of the framer-motion animations are buggy

  const fadeIn = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: {
      duration: 0.3,
      delay: 0.3,
    },
  }

  const extraItemVariants = {
    open: {
      background: COLORS.extraItemsBg,
      transition: {
        duration: 0.5,
        delay: 0.3,
      },
    },
    closed: {
      background: COLORS.sidebarBg,
    },
  }

  // init
  // open menu to page's section when page loads
  useEffect(() => {
    if (currentMenuSectName) setSelectedNav(currentMenuSectName)
  }, [])

  //storing state of the sidebar at client side
  useEffect(() => {
    localStorage.setItem("SIDEBAR_STATE", sidebarOpen)
  }, [sidebarOpen])

  useEffect(() => {
    getPermissions()
  }, [userPermissions])

  const getPermissions = () => {
    if (userPermissions) {
      let temp_permissions = {
        // Studies
        canCreateStudy: userPermissions["study.create"],
        canEditAIs: userPermissions["actionitem.edit"],
        // Admin
        canEditPts: userPermissions["project_type.edit"],
        canEditAts: userPermissions["assessment_type.edit"],
        canEditConfigs: userPermissions["configurations.edit"],
        canEditPresets: userPermissions["prime_grid_preset.edit"],
        maskingCreate: userPermissions["masking.create"],
        credentialsCreate: userPermissions["creds.create"],
        canEditUsers: userPermissions["user.edit"] || userPermissions["user.create"],
        canEditMyPrime: userPermissions["clientmyprime.edit"],
        canViewAuditLog: userPermissions["auditlog.view"],
        // canConfigDatasources: permissions["datasource.configure"],
        // canCreateDataload: permissions["dataload.create"],
      }
      setPermissions(temp_permissions)
    }
  }

  const hasSectionPermissions = (navItem) => {
    if (!navItem.items) return true // My Prime
    return navItem.items.some((item) => !item?.permission || permissions[item?.permission])
  }

  const onClickParent = (navItem) => {
    // parent item expands subitems
    if (navItem.items) {
      setSidebarOpen(true)
      setSelectedNav((val) => (val === navItem.name ? "" : navItem.name))
    }
    // parent item navigates to page
    else {
      navigate(navItem.url)
    }
  }

  // listener to update selected item when page not item in pageData
  const currentMenuSectPath = useMemo(() => {
    if (pathname.startsWith("/study/")) return "/studies"
    if (pathname.startsWith("/file/")) return "/files"
    if (pathname.startsWith("/activity/")) return "/activities"
    if (pathname.startsWith("/request/")) return "/requests"
    return pathname
  }, [pathname])

  // Menu item selection:
  const currentMenuSectName = useMemo(() => {
    // match location to navbarData item
    const pageSection = navbarData.find((navItem) => {
      if (navItem?.items) {
        return navItem?.items?.find((subnavItem) => subnavItem.url === currentMenuSectPath)
      }
      return navItem.url === currentMenuSectPath
    })?.name
    if (pageSection) return pageSection
  }, [currentMenuSectPath])

  const navIsSelected = (navItem) => {
    // selectedNav set when page loads
    if (currentMenuSectName === navItem.name) {
      return true
    }
    return false
  }

  const subNavIsSelected = (item) => {
    if (item.url === currentMenuSectPath) return true
    return false
  }

  return (
    <>
      <div
        ref={ref}
        className={`prime-sidebar-container${sidebarOpen ? " open" : " closed"}`}
      >
        <div className="top-elements">
          <Flex
            className="logo-wrapper"
            align="center"
          >
            <Link to="/">
              <Flex
                align="center"
                gap={12}
              >
                <img
                  className="prime-icon"
                  src={pLogoSrc}
                />
                {sidebarOpen && (
                  <img
                    className="prime-logo"
                    src={primeLogoSrc}
                  />
                )}
              </Flex>
            </Link>

            {sidebarOpen ? (
              <Tooltip
                title="Collapse drawer"
                placement="right"
              >
                <div
                  onClick={() => setSidebarOpen((val) => !val)}
                  className={`expand-button${sidebarOpen ? " expanded" : ""}`}
                  role="button"
                >
                  <i className="fa-solid fa-caret-left" />
                </div>
              </Tooltip>
            ) : (
              <Affix>
                <div
                  onClick={() => setSidebarOpen((val) => !val)}
                  className={`expand-button${sidebarOpen ? " expanded" : ""}`}
                  role="button"
                >
                  <Flex
                    align="center"
                    justify="center"
                    className="expand-btn-icon-container"
                  >
                    <Tooltip
                      title="Expand drawer"
                      placement="right"
                    >
                      <i className="fa-solid fa-caret-right" />
                    </Tooltip>
                  </Flex>
                </div>
              </Affix>
            )}
          </Flex>
          <Flex
            className="nav-item-container"
            gap={2}
            vertical
          >
            {navbarData.map((navItem) => {
              if (permissions && hasSectionPermissions(navItem)) {
                return (
                  <div
                    key={navItem.name}
                    className={`nav-item${
                      navIsSelected(navItem) && (!navItem?.items || sidebarOpen === false) ? " selected" : ""
                    }${sidebarOpen ? " expanded" : ""}`}
                  >
                    {navItem?.items ? (
                      <Flex
                        className={`nav-item-parent`}
                        vertical
                        gap={8}
                      >
                        <Flex
                          className="nav-item-heading"
                          justify="space-between"
                          align="center"
                          onClick={() => onClickParent(navItem)}
                        >
                          <Flex
                            justify="flex-start"
                            align="center"
                            gap={10}
                          >
                            <i
                              className={`nav-item-icon ${navItem.icon}${navIsSelected(navItem) ? " selected" : ""}${
                                hasNewItem(navItem) && showNew ? " new" : ""
                              }${sidebarOpen ? " expanded" : ""}`}
                            />
                            <span className="nav-label">{navItem.name}</span>
                            {hasNewItem(navItem) && showNew && sidebarOpen && (
                              <Badge
                                value="NEW"
                                colorName="yellow"
                              />
                            )}
                          </Flex>
                          <Flex className="expand-nav-item-container">
                            {navItem.items ? (
                              navItem.name === selectedNav ? (
                                <i className="fa-solid fa-caret-up" />
                              ) : (
                                <i className="fa-solid fa-caret-down" />
                              )
                            ) : null}
                          </Flex>

                          {hasNewItem(navItem) && showNew && !sidebarOpen && <div className="new-dot" />}
                        </Flex>
                        {navItem?.items && selectedNav === navItem.name && sidebarOpen && (
                          <Flex
                            vertical
                            className="nav-sub-item"
                          >
                            {navItem.items.map((item) => {
                              subNavIsSelected(item)
                              if (!item.permission || permissions[item.permission])
                                return (
                                  <Link
                                    key={item.name}
                                    className={`subnav-item`}
                                    to={item.url}
                                  >
                                    <div className={`${subNavIsSelected(item) ? "selected" : null} subnav-label`}>
                                      {item.name}
                                      {!!item.isNew && showNew && <div className="new-dot" />}
                                    </div>
                                  </Link>
                                )
                              return null
                            })}
                          </Flex>
                        )}
                      </Flex>
                    ) : (
                      <Flex
                        className={`nav-item-parent no-sub-nav-items`}
                        vertical
                        gap={8}
                      >
                        <Link
                          className="nav-item-heading"
                          to={navItem.url}
                        >
                          <Flex
                            justify="flex-start"
                            align="center"
                            gap={10}
                          >
                            <i
                              className={`nav-item-icon ${navItem.icon}${navIsSelected(navItem) ? " selected" : ""}${
                                hasNewItem(navItem) && showNew ? " new" : ""
                              }${sidebarOpen ? " expanded" : ""}`}
                            />
                            <span className="nav-label">{navItem.name}</span>
                            {hasNewItem(navItem) && showNew && sidebarOpen && (
                              <Badge
                                value="NEW"
                                colorName="yellow"
                              />
                            )}
                          </Flex>

                          {hasNewItem(navItem) && showNew && !sidebarOpen && <div className="new-dot" />}
                        </Link>
                      </Flex>
                    )}
                  </div>
                )
              }
            })}
          </Flex>
        </div>
        <div className="bottom-elements">
          <div className={`sidebar-extras${sidebarOpen ? " expanded" : ""}`}>
            <motion.div
              variants={extraItemVariants}
              animate={sidebarOpen ? "open" : "closed"}
              onClick={() => window.open("https://xogenehelp.zendesk.com/hc/en-us", "_blank")}
              className="know-base"
            >
              <i className="fal fa-graduation-cap" />
              {sidebarOpen && (
                <motion.div
                  {...fadeIn}
                  className="extra-label"
                >
                  Knowledge Base
                </motion.div>
              )}
            </motion.div>
            <motion.div
              variants={extraItemVariants}
              animate={sidebarOpen ? "open" : "closed"}
              onClick={() => window.open("https://xogenehelp.zendesk.com/hc/en-us/requests/new", "_blank").focus()}
              className="suggest-feature"
            >
              <i className="fal fa-lightbulb-on" />
              {sidebarOpen && (
                <motion.div
                  {...fadeIn}
                  className="extra-label"
                >
                  Suggest a Feature
                </motion.div>
              )}
            </motion.div>
          </div>
          <Flex
            className="sidebar-copyright"
            justify="center"
            align="center"
          >
            <span className="copyright">&copy; {dayjs().format("YYYY")} Xogene Services LLC |&nbsp;</span>
            <span>V{publicEnvVars.REACT_APP_PRIME_VERSION}</span>
          </Flex>
        </div>
      </div>
    </>
  )
})

Sidebar.propTypes = {
  navigate: PropTypes.func,
  pathname: PropTypes.string,
}

export default Sidebar
